import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { useContextProvider } from "../page-context/ContextProvider";
import AgGrid from "../grid/AgGrid";
import CloseButton from "../button/CloseButton";
import { Convert, GetUnitCategory } from "../Unit";
import { LuUpload, LuCheck, LuTrash2, LuInfo, LuX } from "react-icons/lu";
import Tooltip from "@mui/material/Tooltip";

// Assuming API calls will be added here
const BOMReviewDialog = ({ open, onClose, parsedData, onConfirm }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [items, setItems] = useState([]);
  
  useEffect(() => {
    if (parsedData && parsedData.items) {
      setItems(parsedData.items.map((item, idx) => {
        let factor = 1;
        let requiresAttention = item.action_required || false;

        const isMatched = ["success", "warning", "converted", "estimated", "incomplete", "review"].includes(item.mapping_status);
        
        if (isMatched) {
          // Priority 1: Use backend's pre-calculated factor (covers all cases)
          if (item.ai_estimated_factor !== undefined && item.ai_estimated_factor !== null) {
            factor = item.ai_estimated_factor;
          } 
          // Priority 2: Frontend fallback for edge cases
          else if (item.raw_unit && item.official_unit) {
            const raw = item.raw_unit.toLowerCase();
            const off = item.official_unit.toLowerCase();
            if (raw === off) {
              factor = 1;
            } else {
              let directConv = Convert(1, raw, off);
              if (directConv !== -1) {
                factor = directConv;
              } else {
                const rawCat = GetUnitCategory(raw);
                const offCat = GetUnitCategory(off);
                const density = item.density;
                if (rawCat === "volume" && offCat === "weight" && density) {
                   const volToM3 = Convert(1, raw, "m3");
                   const kgToOff = Convert(1, "kg", off);
                   factor = (volToM3 !== -1 && kgToOff !== -1) ? volToM3 * density * kgToOff : null;
                   requiresAttention = factor === null;
                } else if (rawCat === "weight" && offCat === "volume" && density && density !== 0) {
                   const weightToKg = Convert(1, raw, "kg");
                   const m3ToOff = Convert(1, "m3", off);
                   factor = (weightToKg !== -1 && m3ToOff !== -1) ? (weightToKg / density) * m3ToOff : null;
                   requiresAttention = factor === null;
                } else {
                  factor = null;
                  requiresAttention = true;
                }
              }
            }
          }
        }

        return {
          ...item, 
          id: idx,
          conversion_factor: factor,
          requires_attention: requiresAttention,
          skipped: false
        };
      }));
    }
  }, [parsedData]);

  const handleConfirm = () => {
    // Exclude skipped items
    const finalItems = items.filter(it => !it.skipped);
    onConfirm(finalItems);
  };

  const handleSkipRow = (id) => {
    setItems((prev) => prev.map(it => it.id === id ? { ...it, skipped: !it.skipped } : it));
  };

  const onCellValueChanged = (params) => {
    const { data } = params;
    setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
  };

  const columnDefs = [
    {
      headerName: "Status",
      field: "mapping_status",
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      suppressSizeToFit: true,
      cellRenderer: (params) => {
        const STATUS_CONFIG = {
          success:    { label: "Arivu_DB",      bg: '#16a34a15', color: '#16a34a', border: '#16a34a33' },
          converted:  { label: "Arivu_DB",      bg: '#16a34a15', color: '#16a34a', border: '#16a34a33' },
          estimated:  { label: "AI_Estimated",  bg: '#f59e0b15', color: '#d97706', border: '#f59e0b33' },
          warning:    { label: "Needs_Review",  bg: '#ef444415', color: '#ef4444', border: '#ef444433' },
          review:     { label: "Needs_Review",  bg: '#ef444415', color: '#ef4444', border: '#ef444433' },
          incomplete: { label: "Needs_Review",  bg: '#ef444415', color: '#ef4444', border: '#ef444433' },
          new:        { label: "New",           bg: isDark ? '#ffffff10' : '#f1f5f9', color: isDark ? '#94a3b8' : '#64748b', border: isDark ? '#ffffff20' : '#cbd5e1' },
        };
        const cfg = STATUS_CONFIG[params.value] || STATUS_CONFIG.new;
        const tooltip = params.data.reason || params.data.ai_conversion_reasoning || "";
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 1 }}>
            <Tooltip title={tooltip} placement="top" arrow disableHoverListener={!tooltip}>
              <Box sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: cfg.bg,
                color: cfg.color, 
                border: `1px solid ${cfg.border}`,
                px: 1, 
                height: '24px',
                borderRadius: "4px", 
                fontSize: "11px", 
                fontWeight: 700,
                textTransform: 'uppercase',
                cursor: tooltip ? 'help' : 'default',
                lineHeight: 1,
                mr: 2
              }}>
                {cfg.label}
              </Box>
            </Tooltip>
          </Box>
        );
      }
    },
    { 
      headerName: "Extracted Name", 
      field: "raw_name", 
      width: 200,
      minWidth: 180,
      maxWidth: 220,
      suppressSizeToFit: true,
      wrapText: true,
      autoHeight: true,
      tooltipField: "raw_name",
    },
    { 
      headerName: "Matched Library Item", 
      field: "matched_description", 
      flex: 1,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      tooltipField: "matched_description",
      valueFormatter: (p) => p.value || "-" 
    },
    { headerName: "Qty", field: "quantity", width: 75, minWidth: 75, maxWidth: 75, suppressSizeToFit: true },
    { headerName: "Raw Unit", field: "raw_unit", width: 100, minWidth: 100, maxWidth: 110, suppressSizeToFit: true },
    { headerName: "Lib Unit", field: "official_unit", width: 90, minWidth: 90, maxWidth: 100, suppressSizeToFit: true, valueFormatter: (p) => p.value || "-" },
    { 
      headerName: "Unit Conversion", 
      field: "conversion_factor", 
      width: 180,
      cellStyle: { display: 'flex', alignItems: 'stretch', padding: 0 },
      editable: true,
      cellClass: "editable-cell",
      valueParser: (params) => Number(params.newValue),
      cellRenderer: (params) => {
        const hasReasoning = !!params.data.ai_conversion_reasoning;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
            <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${tok.border.table}`,
                borderRadius: '6px',
                height: '32px',
                overflow: 'hidden'
            }}>
                <Typography sx={{ px: 1.5, fontSize: '13px', fontWeight: 600 }}>
                    {params.value || "-"}
                </Typography>
                {hasReasoning && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{ height: '32px' }} />
                        <Tooltip title={params.data.ai_conversion_reasoning} placement="top" arrow>
                            <Box sx={{ 
                                px: 1, 
                                display: 'flex', 
                                alignItems: 'center', 
                                color: isDark ? '#4ade80' : '#659d33', 
                                cursor: 'help'
                            }}>
                                <LuInfo size={14} />
                            </Box>
                        </Tooltip>
                    </>
                )}
            </Box>
          </Box>
        );
      }
    },
    { 
      headerName: "GWP / Unit", 
      field: "gwp_unit", 
      width: 180,
      cellStyle: { display: 'flex', alignItems: 'stretch', padding: 0 },
      editable: true,
      cellClass: "editable-cell",
      valueParser: (params) => Number(params.newValue),
      cellRenderer: (params) => {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
            <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${tok.border.table}`,
                borderRadius: '6px',
                height: '32px',
                overflow: 'hidden'
            }}>
                <Typography sx={{ px: 1.5, fontSize: '13px', fontWeight: 600 }}>
                    {params.value || 0}
                </Typography>
                {params.data.ai_estimated && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{ height: '32px' }} />
                        <Box sx={{ 
                            px: 1,
                            fontSize: '9px', 
                            backgroundColor: isDark ? 'rgba(74,222,128,0.2)' : '#e2e8f0', 
                            color: isDark ? '#4ade80' : '#475569', 
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 700,
                        }}>
                            AI
                        </Box>
                    </>
                )}
            </Box>
          </Box>
        );
      }
    },
    {
      headerName: "Source",
      field: "gwp_reference",
      width: 150,
      minWidth: 130,
      maxWidth: 180,
      suppressSizeToFit: true,
      cellRenderer: (params) => {
        const ref = params.value;
        const isDb = ref === "Arivu DB";
        const label = ref || "—";
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 1 }}>
            <Tooltip title={ref || "No source recorded"} placement="top" arrow>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDb
                  ? (isDark ? '#16a34a20' : '#dcfce7')
                  : (isDark ? '#f59e0b20' : '#fef3c7'),
                color: isDb
                  ? (isDark ? '#4ade80' : '#15803d')
                  : (isDark ? '#fbbf24' : '#92400e'),
                border: `1px solid ${isDb ? '#16a34a33' : '#f59e0b33'}`,
                px: 1,
                height: '22px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                cursor: 'help',
                maxWidth: '140px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
                {isDb ? '✓ Arivu DB' : label}
              </Box>
            </Tooltip>
          </Box>
        );
      }
    },

    { 
      headerName: "Biogenic GWP", 
      field: "biogenic_gwp", 
      width: 130,
      minWidth: 120,
      editable: true,
      cellClass: "editable-cell",
      valueParser: (params) => Number(params.newValue),
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography sx={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: params.value < 0 ? '#16a34a' : 'inherit' 
            }}>
                {params.value || 0}
            </Typography>
        </Box>
      )
    },
    { 
      headerName: "Uncertainty", 
      field: "uncertainty_label", 
      width: 100,
      minWidth: 90,
      cellRenderer: (params) => {
        const val = params.data.uncertainty || 1.0;
        const pct = Math.round((val - 1) * 100);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Box sx={{ 
                px: 1, 
                py: 0.25, 
                borderRadius: '4px', 
                backgroundColor: pct > 0 ? 'rgba(245,158,11,0.1)' : 'transparent',
                color: pct > 0 ? '#d97706' : 'inherit',
                fontSize: '11px',
                fontWeight: 700
            }}>
                {pct > 0 ? `+${pct}%` : "0%"}
            </Box>
          </Box>
        );
      }
    },
    {
      headerName: "Action",
      field: "action",
      width: 80,
      cellRenderer: (params) => {
        const isSkipped = params.data.skipped;
        return (
          <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Button 
                size="small" 
                onClick={() => handleSkipRow(params.data.id)}
                sx={{ 
                  minWidth: 'auto', 
                  p: 0.5, 
                  color: isSkipped ? tok.text.tabActive : "#ef4444",
                  '&:hover': { backgroundColor: 'transparent', opacity: 0.7 } 
                }}
            >
                {isSkipped ? <LuCheck size={20} /> : <LuX size={20} />}
            </Button>
          </Box>
        );
      }
    }
  ];

  const unresolvedCount = items.filter(it => !it.skipped && it.requires_attention && (it.conversion_factor === null || isNaN(it.conversion_factor))).length;
  const skippedCount = items.filter(it => it.skipped).length;

  if (!parsedData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{
      sx: {
        backgroundColor: tok.bg.page,
        borderRadius: "16px",
        overflow: "hidden",
        height: '85vh'
      }
    }}>
      <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: tok.bg.card }}>
        <Typography sx={{ ...tok.sectionHeading, borderBottom: 'none', pb: 0, m: 0 }}>
          {t("BOM Review")}
        </Typography>
        <CloseButton onClick={onClose} />
      </Box>
      <Divider />
      {unresolvedCount > 0 && (
        <Box sx={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', p: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LuInfo size={20} color="#ef4444" />
          <Typography sx={{ color: isDark ? '#ef4444' : '#b91c1c', fontSize: '14px', fontWeight: 600 }}>
            {unresolvedCount} items require your attention for unit conversion. Please input a factor or click "Skip".
          </Typography>
        </Box>
      )}
      <DialogContent sx={{ p: 0, backgroundColor: tok.bg.page, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, p: 3 }}>
           <Box sx={{ 
             height: '100%', 
             width: '100%', 
             borderRadius: '12px', 
             border: unresolvedCount > 0 ? `1.5px solid #ef4444` : `1.5px solid ${tok.border.table}`, 
             overflow: 'hidden' 
           }}>
             <AgGrid
               rowData={items}
               columnDefs={columnDefs}
               onCellValueChanged={onCellValueChanged}
               pagination={true}
               paginationPageSize={10}
               domLayout="normal"
               
               sx={{
                 "& .ag-row-skipped": {
                   opacity: 0.4,
                   backgroundColor: isDark ? "#27272a !important" : "#f1f5f9 !important",
                   textDecoration: "line-through"
                 },
                 "& .ag-row-attention": {
                   backgroundColor: isDark ? "rgba(239, 68, 68, 0.1) !important" : "rgba(239, 68, 68, 0.05) !important",
                 }
               }}
               getRowClass={(params) => {
                 if (params.data.skipped) return "ag-row-skipped";
                 if (params.data.requires_attention && (params.data.conversion_factor === null || isNaN(params.data.conversion_factor))) return "ag-row-attention";
                 return "";
               }}
             />
           </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, px: 3, backgroundColor: tok.bg.card, justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '13px', color: tok.text.secondary }}>
          {items.length - skippedCount} items to save, {skippedCount} skipped.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onClose} sx={{ color: tok.text.secondary }}>
            {t("Cancel")}
          </Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained" 
            sx={{ backgroundColor: tok.bg.tabActive, '&:hover': { backgroundColor: tok.bg.tabActive }, '&:disabled': { backgroundColor: isDark ? '#3f3f46' : '#cbd5e1' } }}
          >
            {t("Confirm & Save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BOMReviewDialog;
